import cloneDeep from 'lodash/cloneDeep';
import { fetchRfiService as fetchWithToken } from '../utils/fetchRfiService';
import { mapRequestStatusFiltersToServerFilters } from '../../helpers/statusHelpers';
import { getApiBaseUrl } from '../config';
import { HttpMethod, headers } from '../constants';
import {
  Rfi,
  RfiId,
  SuggestedCustomIdentifierResponse,
  CreateRfiRequest,
  GetRfisRequest,
  UpdateRfiRequest,
  GetRfisResponse,
  CommentsList,
  Comment,
  GetCommentsRequest,
  GetActivitiesRequest,
  ListActivitiesResponse,
  CreateLinkRequest,
  CreateLinkResponse,
  RemoveLinkRequestDefinition,
  RemoveLinkResponse,
  NextCustomIdentifierResponseDefinition,
  NudgeResponseDefinition,
  AssigneeResponse,
  CreateAssigneeResponseRequest,
  CreateAssigneeResponseResponse,
  UpdateRfiResponse,
  GetRfiAttachmentsRequest,
  GetAttachmentsResponse,
  CreateFormSuggestionsResponse,
  ShareRfiRequest,
  ShareRfiResponse,
} from '../types';
import { SearchRfiRequest, ServerSearchRfiRequest } from '../types/requests/searchRfis';
import { constructFilterParamList, getFlatParams } from '../utils/fetchHelpers';
import { CreateSDKConfig } from './types';
import { GenerateAiRfiResponse } from '../types/responses/generateAiRfi';
import {
  AiGenerateRfiJobResult,
  AiRfiJobType,
  GetAiJobStatusResponse,
} from '../types/responses/getAiJobStatus';

const RFIsApi = ({ projectId }: CreateSDKConfig) => {
  const API_URL = getApiBaseUrl(projectId);
  return {
    get(id: RfiId): Promise<Rfi> {
      const url = `${API_URL}/rfis/${id}`;
      return fetchWithToken<Rfi>(url);
    },
    getAll(getRfiRequest: GetRfisRequest): Promise<GetRfisResponse> {
      const { limit, offset, sortBy, filterBy, fields } = getRfiRequest;
      const url = `${API_URL}/rfis`;
      const searchParams = new URLSearchParams();
      if (limit) {
        searchParams.set('limit', limit.toString());
      }
      if (offset) {
        searchParams.set('offset', offset.toString());
      }
      if (sortBy) {
        searchParams.set('sort', getFlatParams(sortBy));
      }
      if (filterBy) {
        const filterParamList = constructFilterParamList(filterBy);
        filterParamList.forEach(([param, value]) => {
          searchParams.set(param, value);
        });
      }
      if (fields) {
        searchParams.set('fields', getFlatParams(fields));
      }
      const search = searchParams.toString();
      return fetchWithToken<GetRfisResponse>(search ? `${url}?${search}` : url);
    },
    search(searchRfiRequest: SearchRfiRequest): Promise<GetRfisResponse> {
      const url = `${API_URL}/search:rfis`;
      const mappedSearchRfiRequest: ServerSearchRfiRequest = cloneDeep(
        searchRfiRequest,
      ) as ServerSearchRfiRequest;

      const statusFilter = searchRfiRequest.filter?.status;
      if (statusFilter) {
        mappedSearchRfiRequest.filter!.status =
          mapRequestStatusFiltersToServerFilters(statusFilter);
      }

      return fetchWithToken<GetRfisResponse>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify(mappedSearchRfiRequest),
        headers,
      });
    },
    async sendNudge(rfiId: RfiId): Promise<NudgeResponseDefinition> {
      const url = `${getApiBaseUrl(projectId)}/rfis:nudge-notification`;
      return fetchWithToken<NudgeResponseDefinition>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify({ rfiId }),
        headers,
      });
    },
    customIdentifier: {
      getSuggested(): Promise<SuggestedCustomIdentifierResponse> {
        const url = `${API_URL}/rfis/custom-identifier`;
        return fetchWithToken<SuggestedCustomIdentifierResponse>(url).then((res) => res);
      },
      validate(customIdentifier: string): Promise<boolean> {
        const url = `${API_URL}/rfis/custom-identifier:validate`;
        return fetchWithToken(url, {
          method: HttpMethod.Post,
          body: JSON.stringify({ customIdentifier }),
          headers,
        })
          .then(() => Promise.resolve(true))
          .catch(() => Promise.resolve(false));
      },
      getRevision(rfiId: RfiId): Promise<NextCustomIdentifierResponseDefinition> {
        const url = `${API_URL}/rfis:next-revision`;
        return fetchWithToken<NextCustomIdentifierResponseDefinition>(url, {
          method: HttpMethod.Post,
          body: JSON.stringify({ rfiId }),
          headers,
        });
      },
    },
    create(rfiAttributes: CreateRfiRequest): Promise<Rfi> {
      const url = `${API_URL}/rfis`;
      return fetchWithToken<Rfi>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify(rfiAttributes),
        headers,
      });
    },
    update(id: string, body: UpdateRfiRequest): Promise<UpdateRfiResponse> {
      const url = `${API_URL}/rfis/${id}`;
      return fetchWithToken<Rfi>(url, {
        method: HttpMethod.Patch,
        body: JSON.stringify(body),
        headers,
      });
    },
    getComments(getCommentsRequest: GetCommentsRequest): Promise<CommentsList> {
      const { rfiId, limit, offset, sort, filter, fields } = getCommentsRequest;
      const url = `${API_URL}/rfis/${rfiId}/comments`;
      const searchParams = new URLSearchParams();
      if (limit) {
        searchParams.set('limit', limit.toString());
      }
      if (offset) {
        searchParams.set('offset', offset.toString());
      }
      if (sort) {
        searchParams.set('sort', getFlatParams(sort));
      }
      if (filter) {
        const filterParamList = constructFilterParamList(filter);
        filterParamList.forEach(([param, value]) => {
          searchParams.set(param, value);
        });
      }
      if (fields) {
        searchParams.set('fields', getFlatParams(fields));
      }
      const search = searchParams.toString();
      return fetchWithToken<CommentsList>(search ? `${url}?${search}` : url);
    },
    createComment(rfiId: RfiId, comment: string): Promise<Comment> {
      const url = `${API_URL}/rfis/${rfiId}/comments`;
      return fetchWithToken<Comment>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify({ body: comment }),
        headers,
      });
    },
    getActivities(getAttachmentsRequest: GetActivitiesRequest): Promise<ListActivitiesResponse> {
      const { rfiId, limit, nextToken, xUserId, filter } = getAttachmentsRequest;
      const url = `${API_URL}/rfis/${rfiId}/activities`;
      const searchParams = new URLSearchParams();
      if (limit) {
        searchParams.set('limit', limit.toString());
      }
      if (nextToken) {
        searchParams.set('nextToken', nextToken);
      }
      if (xUserId) {
        searchParams.set('sort', getFlatParams(xUserId));
      }
      if (filter) {
        const filterParamList = constructFilterParamList(filter);
        filterParamList.forEach(([param, value]) => {
          searchParams.set(param, value);
        });
      }

      const search = searchParams.toString();
      return fetchWithToken<ListActivitiesResponse>(search ? `${url}?${search}` : url);
    },
    createLinks(rfiId: RfiId, links: CreateLinkRequest): Promise<CreateLinkResponse> {
      const url = `${API_URL}/rfis/${rfiId}/links`;
      return fetchWithToken<CreateLinkResponse>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify(links),
        headers,
      });
    },
    removeLinks(rfiId: RfiId, links: RemoveLinkRequestDefinition): Promise<RemoveLinkResponse> {
      const url = `${API_URL}/rfis/${rfiId}/links:delete`;
      return fetchWithToken<RemoveLinkResponse>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify(links),
        headers,
      });
    },
    addResponse(rfiId: RfiId, createAssigneeResponseRequest: CreateAssigneeResponseRequest) {
      const url = `${API_URL}/rfis/${rfiId}/responses`;
      return fetchWithToken<CreateAssigneeResponseResponse>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify(createAssigneeResponseRequest),
        headers,
      });
    },
    updateResponse(
      rfiId: RfiId,
      responsId: AssigneeResponse['id'],
      createAssigneeResponseRequest: CreateAssigneeResponseRequest,
    ) {
      const url = `${API_URL}/rfis/${rfiId}/responses/${responsId}`;
      return fetchWithToken<CreateAssigneeResponseResponse>(url, {
        method: HttpMethod.Patch,
        body: JSON.stringify(createAssigneeResponseRequest),
        headers,
      });
    },
    getAttachments(
      getAttachmentsRequest: GetRfiAttachmentsRequest,
    ): Promise<GetAttachmentsResponse> {
      const { rfiId, limit, offset } = getAttachmentsRequest;
      const url = `${API_URL}/rfis/${rfiId}/attachments`;
      const searchParams = new URLSearchParams();
      if (limit) {
        searchParams.set('limit', limit.toString());
      }
      if (offset) {
        searchParams.set('offset', offset.toString());
      }
      const search = searchParams.toString();
      return fetchWithToken<GetAttachmentsResponse>(search ? `${url}?${search}` : url);
    },
    getCreateFormSuggestions(prompt: string): Promise<CreateFormSuggestionsResponse> {
      const searchParams = new URLSearchParams();
      searchParams.set('prompt', prompt);
      const url = `${API_URL}/rfis:create-form-suggestions?${searchParams.toString()}`;

      return fetchWithToken<CreateFormSuggestionsResponse>(url);
    },
    share(rfiId: RfiId, shareRfiRequest: ShareRfiRequest): Promise<ShareRfiResponse> {
      const url = `${API_URL}/rfis/${rfiId}/bridge`;
      return fetchWithToken(url, {
        method: HttpMethod.Post,
        body: JSON.stringify(shareRfiRequest),
        headers,
      });
    },
    generateAiRFI(
      prompt: string,
      jobType: AiRfiJobType = 'createRfi',
    ): Promise<GenerateAiRfiResponse> {
      const url = `${API_URL}/ai/generate:rfi`;
      return fetchWithToken<GenerateAiRfiResponse>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify({ prompt, jobType }),
        headers,
      });
    },
    getAiJobStatus<TResult = AiGenerateRfiJobResult>(
      jobId: string,
    ): Promise<GetAiJobStatusResponse<TResult>> {
      const url = `${API_URL}/ai/${jobId}`;
      return fetchWithToken<GetAiJobStatusResponse<TResult>>(url);
    },
    createAIGeneratedResponse(
      rfiId: RfiId,
      feedbacks: string[],
    ): Promise<CreateAssigneeResponseResponse> {
      const url = `${API_URL}/rfis/${rfiId}/responses/hackweek-genai`;
      return fetchWithToken<CreateAssigneeResponseResponse>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify({ feedbacks }),
        headers,
      });
    },
    async checkAiEnablement(): Promise<boolean> {
      const url = `${API_URL}/ai/enablement`;
      try {
        await fetchWithToken(url);
        return true;
      } catch (error) {
        if ((error as { status?: number }).status === 403) {
          return false;
        }
        throw error;
      }
    },
  };
};

export default RFIsApi;
